/* @flow */

import type { BO_API_REQUEST_RESPONSE_BASE } from '../../../../../libs/netgemLibrary/videofutur/types/RequestResponseBase';
import type { CombinedReducers } from '../../../../reducers';
import type { Dispatch } from '../../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../../emitter';
import type { BO_RESPONSE_WITH_ETAG } from '../types/common';
import type { BO_API_GET_STREAMS_TYPE, BO_API_OPEN_STREAM_TYPE } from '../types/stream';

import { logError } from '../../../../../helpers/debug/debug';
import Localizer from '../../../../../helpers/localization';
import { createCustomNetworkErrorFromKey } from '../../../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import sendGetStreamsRequest from '../getStreams';
import sendStreamStopRequest from '../streamStop';

const stopStreamsForThisDevice: (distributorId: string, streams: Array<BO_API_OPEN_STREAM_TYPE>, applicationId: string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (distributorId, streams, applicationId, bookmark, signal) =>
  (dispatch: Dispatch): Promise<Array<PromiseSettledResult<BO_RESPONSE_WITH_ETAG>>> => {
    const promises: Array<Promise<BO_RESPONSE_WITH_ETAG>> = streams
      .filter(({ data }) => data.appId === applicationId)
      .map(({ data }) => dispatch(sendStreamStopRequest(distributorId, data.vstId, undefined, signal)));

    return Promise.allSettled(promises);
  };

const stopOpenStreams: (distributorId: string | null, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (distributorId, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<number> => {
    const state = getState();
    const {
      appConfiguration: { mainDistributorId },
      appRegistration: { applicationId },
    } = state;

    if (!applicationId) {
      return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.missing_application_id'));
    }

    const distributor = distributorId ?? mainDistributorId;

    if (!distributor) {
      return Promise.reject(createCustomNetworkErrorFromKey('common.messages.errors.missing_distributor_id'));
    }

    return dispatch(sendGetStreamsRequest(distributor, signal)).then((response: BO_API_REQUEST_RESPONSE_BASE) => {
      const {
        body,
        body: { length },
      } = response as BO_API_GET_STREAMS_TYPE;

      if (length > 0) {
        // At least 1 stream has been left open
        dispatch(stopStreamsForThisDevice(distributor, body, applicationId, signal)).catch(() => logError(Localizer.localize('common.messages.errors.streams_stop_failure')));
      }

      return Promise.resolve(length);
    });
  };

export { stopOpenStreams };
